<template>
  <div>
    <VSelect
      v-model="defaultAnalysis.networkType"
      :label="$t('forms.labels.networkTypes')"
      :items="displayedNetworkTypes"
      :loading="loading"
      data-cy="DefaultAnalysisNetwork"
      @input="updateData"
    />
    <VSelect
      v-model="defaultAnalysis.componentType"
      :label="$t('forms.labels.componentTypes')"
      :items="displayedComponentTypes"
      :required="true"
      :loading="loading"
      data-cy="DefaultAnalysisComponent"
      @input="updateData"
    />
    <VAutocomplete
      v-model="defaultAnalysis.analysisTypes"
      :label="$t('forms.labels.analysisTypes')"
      :items="displayedAnalysisTypes"
      :multiple="true"
      :rules="{ required: true }"
      :loading="loading || analysisTypesLoading"
      data-cy="DefaultAnalysisTypes"
      @input="updateData"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete.vue';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import { isEmpty, cloneDeep } from 'lodash';

export default {
    components: {
        VAutocomplete,
        VSelect,
    },
    props: {
        analysis: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            defaultAnalysis: {
                networkType: null,
                componentType: null,
                analysisTypes: null,
            },
            networkTypes: [],
            componentTypes: [],
            analysisTypes: [],
            defaultAnalysisTypes: [],
            loading: false,
            analysisTypesLoading: false,
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'teamMainLang',
        ]),
        displayedAnalysisTypes() {
            return this.analysisTypes.map(analysisType => ({
                name: analysisType.name[this.userLang] || analysisType.name[this.teamMainLang],
                id: analysisType.id,
            }));
        },
        defaultAnalysisTypesById() {
            return this.defaultAnalysisTypes
                .reduce((defaultAnalysisTypesById, defaultAnalysisType) => ({
                    ...defaultAnalysisTypesById,
                    [defaultAnalysisType.analysisType.id]: defaultAnalysisType.analysisType.id,
                }), {});
        },
        displayedNetworkTypes() {
            return this.$utils.ascSort(this.networkTypes.map(networkType => ({
                name: networkType.name[this.userLang] || networkType.name[this.teamMainLang],
                id: networkType.id,
            })));
        },
        displayedComponentTypes() {
            return this.$utils.ascSort(this.componentTypes.map(componentType => ({
                name: componentType.name[this.userLang] || componentType.name[this.teamMainLang],
                id: componentType.id,
            })));
        },
        networkType() {
            return this.defaultAnalysis.networkType;
        },
        componentType() {
            return this.defaultAnalysis.componentType;
        },
    },
    watch: {
        networkType() {
            if (this.defaultAnalysis.componentType) {
                this.analysisTypesLoading = true;
                this.getDefaultAnalysisTypes().then(defaultAnalysisTypes => {
                    this.defaultAnalysisTypes = defaultAnalysisTypes;
                    this.analysisTypesLoading = false;
                });
            }
        },
        componentType() {
            this.analysisTypesLoading = true;
            this.getDefaultAnalysisTypes().then(defaultAnalysisTypes => {
                this.defaultAnalysisTypes = defaultAnalysisTypes;
                this.analysisTypesLoading = false;
            });
        },
    },
    async created() {
        if (!isEmpty(this.analysis)) {
            this.defaultAnalysis = cloneDeep(this.analysis);
            const defaultAnalysisTypes = await this.getDefaultAnalysisTypes();
            this.defaultAnalysis.analysisTypes = defaultAnalysisTypes.flatMap(defaultAnalysis => defaultAnalysis.analysisType.id);
        }
        this.getData();
    },
    methods: {
        async getData() {
            this.networkTypes = await this.getNetworkTypes();
            this.componentTypes = await this.getComponentTypes();
            this.analysisTypes = await this.getAnalysisTypes();
            this.loading = false;
        },
        async getNetworkTypes() {
            const { data } = await this.$rq.getNetworkTypes();
            return data.networkTypes;
        },
        async getComponentTypes() {
            const { data } = await this.$rq.getComponentTypes({ pagination: false });
            return data.componentTypes;
        },
        async getAnalysisTypes() {
            const { data: { types } } = await this.$rq.getAnalysisTypes({ typeName: 'All', pagination: false });
            return types;
        },
        async getDefaultAnalysisTypes() {
            const queryParams = {
                networkType: this.defaultAnalysis.networkType || 'any',
                componentType: this.defaultAnalysis.componentType,
            };
            const { data } = await this.$rq.getDefaultAnalyses(queryParams);
            return data.defaultAnalysisTypes;
        },
        updateData() {
            this.$emit('sendData', this.defaultAnalysis);
        },
    },
};
</script>
